import IconsGrid from "components/Icons/IconcsGrid";
import SubscriptionPlans from "components/Subscription/Subscription";
import { OWNER_BENEFITS, OWNER_EVENTS_DATA, OWNER_FAQ, OWNER_HOW_IT_WORKS } from "data/constants";
import { useAnimation, useInView } from "framer-motion";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CallToActionHeader from "./CallToActionHeader";
import CallToFaqSection from "./CallToFaqSection";
import HowItWorkCallToAction from "./HowItWorkCallToAction";
import OwnerCallToActionImage from "./OwnerCallToActionImage";
import { Helmet } from "react-helmet";

const OwnerCallToAction = () => {
  const benefitsRef = useRef(null);
  const callToActionImageRef = useRef(null);

  const benefitsControls = useAnimation();
  const callToActionImageControls = useAnimation();

  const isBenefitsInView = useInView(benefitsRef, { once: true, margin: "-100px" });
  const isCallToActionImageInView = useInView(callToActionImageRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isBenefitsInView) {
      benefitsControls.start("visible");
    }
  }, [isBenefitsInView, benefitsControls]);

  useEffect(() => {
    if (isCallToActionImageInView) {
      callToActionImageControls.start("visible");
    }
  }, [isCallToActionImageInView, callToActionImageControls]);
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>
          Espaces événementiels Paris, Île-de-France et partout en France : trouvez le lieu idéal | Event To Bee{" "}
        </title>
        <meta
          name="description"
          content="Des espaces événementiels : salles de réception, jardins, lofts, studios, popup store, lieux atypiques. Des lieux pour tous vos événements privés et professionnels."
        />
      </Helmet>

      <div>
        <div className="bg-gradient-to-br from-gray-50 to-gray-200">
          <CallToActionHeader
            title="Attirez plus de réservations en publiant votre espace"
            highlightText="espace"
            highlightTextColor="bg-yellow-300"
            description="Rejoignez EventToBee et donnez de la visibilité à votre espace auprès de milliers de clients à la recherche du lieu parfait pour leur événement. "
            buttonText="Commencer"
            buttonAction={() => navigate("/add-location")}
            imageSrc={`${process.env.REACT_APP_CLOUD_FRONT + "static-files/ajouter-votre-espace-07.png"}  `}
            imageWidth="w-96"
            imageHeight="w-96"
          />
        </div>
        <div className="px-4 py-16 mt-20 dark:bg-neutral-800" ref={benefitsRef}>
          {/* Section Title */}
          <div className="mb-4 text-xl font-extrabold text-center text-gray-800 md:text-3xl dark:text-white">
            <h2>Les bonnes raisons de référencer votre espace sur EventToBee</h2>
          </div>
          <div className="mb-12 text-sm text-center text-gray-600 dark:text-white">
            <h3>Les bonnes raisons de référencer votre espace sur EventToBee</h3>
          </div>

          {/* Benefits Section */}
          <div className="flex flex-col items-center justify-between gap-8 mx-auto md:flex-row md:flex-wrap max-w-7xl">
            {OWNER_BENEFITS.map((value) => (
              <div key={value.id} className="flex justify-center text-center md:w-1/4">
                <div className="p-6 rounded-xl flex flex-col items-center text-center h-full w-full min-h-[400px] ">
                  {/* ICON */}
                  <div className="h-[100px] flex items-center justify-center">
                    <img src={value.icon} alt={value.title} width={80} height={80} className="mx-auto" />
                  </div>

                  {/* TITLE */}
                  <div className="flex items-center justify-center mt-4">
                    <h5 className="text-xl font-semibold text-gray-800 whitespace-nowrap dark:text-white">
                      {value.title}
                    </h5>
                  </div>

                  {/* DESCRIPTION */}
                  <div className="flex items-center justify-center mt-2">
                    <p className="leading-relaxed text-gray-600 dark:text-white">{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-20">
          <HowItWorkCallToAction
            title={OWNER_HOW_IT_WORKS.title}
            stepCircleColor="bg-eventToBee"
            steps={OWNER_HOW_IT_WORKS.steps}
            titleHighlight=""
          />
        </div>
        <div className="mt-20 dark:bg-neutral-800">
          <IconsGrid
            events={OWNER_EVENTS_DATA}
            title="Chaque lieu est un EventToBee"
            description="Fêtes privées, événements d'entreprise, réunions, séances photo... choisissez vos réservations. Proposez votre espace à des milliers de clients à la recherche de lieux similaires"
          />
        </div>

        <div ref={callToActionImageRef} className="mt-20">
          <OwnerCallToActionImage
            controls={callToActionImageControls}
            title="Votre espace mérite d'être vu"
            description="Découvrez notre sélection d’espaces événementiels disponibles à Paris et en Île-de-France. Que vous cherchiez une salle de réception, un studio, un jardin ou même une péniche, vous trouverez ici le lieu parfait pour tous vos événements, privés ou professionnels"
          />
        </div>

        <SubscriptionPlans />
        <div className="mt-20">
          <CallToFaqSection toggleBgColor="bg-eventToBee" faqs={OWNER_FAQ} />
        </div>
      </div>
    </>
  );
};

export default OwnerCallToAction;
